import React from "react";

const S = {
  wrapper: { marginTop:'10px', marginLeft:'0px', border:'3px', padding:'40px', borderStyle:'solid', borderColor:'grey', fontFamily:"'Georgia', serif", color:'#1a1a2e', lineHeight:'1.7' },
  h1: { fontSize:'2rem', fontWeight:'700', borderBottom:'3px solid #2E75B6', paddingBottom:'8px', marginBottom:'24px', color:'#1F3864' },
  h2: { fontSize:'1.3rem', fontWeight:'700', color:'#2E75B6', marginTop:'36px', marginBottom:'12px', borderLeft:'4px solid #2E75B6', paddingLeft:'10px' },
  p: { marginBottom:'12px', fontSize:'0.97rem' },
  infoBox: { background:'#EBF3FB', border:'2px solid #2E75B6', borderRadius:'6px', padding:'16px 20px', margin:'16px 0' },
  infoBoxTitle: { fontWeight:'700', fontFamily:"'Courier New', monospace", fontSize:'0.88rem', color:'#1F3864', marginBottom:'8px' },
  infoBoxItem: { fontSize:'0.9rem', margin:'4px 0', paddingLeft:'12px', borderLeft:'2px solid #2E75B6' },
  table: { width:'100%', borderCollapse:'collapse', marginBottom:'20px', fontSize:'0.92rem' },
  th: { background:'#2E75B6', color:'#fff', padding:'10px 14px', textAlign:'left', fontWeight:'700', fontSize:'0.9rem' },
  tdE: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#f5f9ff' },
  tdO: { padding:'10px 14px', borderBottom:'1px solid #ccd9ea', verticalAlign:'top', background:'#fff' },
  tdL: { fontWeight:'600', color:'#1F3864', whiteSpace:'nowrap', width:'220px' },
  code: { fontFamily:"'Courier New', monospace", fontSize:'0.88rem', whiteSpace:'pre-wrap' },
  dWrap: { margin:'24px 0', background:'#f8fbff', border:'1px solid #ccd9ea', borderRadius:'8px', padding:'20px' },
  dTitle: { fontWeight:'700', fontSize:'0.95rem', color:'#1F3864', marginBottom:'12px', textAlign:'center', letterSpacing:'0.02em' },
  dCaption: { fontSize:'0.84rem', color:'#555', textAlign:'center', marginTop:'10px', fontStyle:'italic' },
};

function InfoBox({ title, items }) {
  return (
    <div style={S.infoBox}>
      <div style={S.infoBoxTitle}>{title}</div>
      {items.map((item, i) => <div key={i} style={S.infoBoxItem}>{item}</div>)}
    </div>
  );
}

function Table2({ rows }) {
  return (
    <table style={S.table}>
      <thead><tr>
        <th style={{ ...S.th, width:'220px' }}>Concept</th>
        <th style={S.th}>Formula / Notes</th>
      </tr></thead>
      <tbody>
        {rows.map(([label, content], i) => (
          <tr key={i}>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.tdL }}>{label}</td>
            <td style={{ ...(i%2===0?S.tdE:S.tdO), ...S.code }}>{content}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 1 — Parallelogram Anatomy
// ─────────────────────────────────────────────────────────────────
function DiagramAnatomy() {
  const W = 520, H = 280;
  // Parallelogram vertices: bottom-left A, bottom-right B, top-right C, top-left D
  // shear offset = 70px, base = 280, height = 130
  const off = 70, base = 280, ht = 130;
  const A = [80, 220], B = [80+base, 220], C = [80+base+off, 220-ht], D = [80+off, 220-ht];

  // midpoints for diagonals
  const mid = [(A[0]+C[0])/2, (A[1]+C[1])/2];

  // angle phi at A
  const phi = Math.atan2(D[1]-A[1], D[0]-A[0]);
  const phiDeg = (Math.atan2(ht, off) * 180 / Math.PI).toFixed(1);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Parallelogram Anatomy — Sides, Height, Angles, and Diagonals</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <defs>
            <marker id="pArr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#888"/></marker>
          </defs>

          {/* Height dashed line */}
          <line x1={B[0]} y1={B[1]} x2={B[0]} y2={C[1]} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="5,3"/>
          <polyline points={(B[0]-10)+","+B[1]+" "+(B[0]-10)+","+(B[1]-10)+" "+B[0]+","+(B[1]-10)} fill="none" stroke="#27ae60" strokeWidth="1.3"/>
          <text x={B[0]+8} y={(B[1]+C[1])/2+4} fontSize="11" fill="#27ae60" fontWeight="700">h</text>

          {/* Parallelogram fill */}
          <polygon points={[A,B,C,D].map(p=>p.join(',')).join(' ')} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2.5"/>

          {/* Diagonals */}
          <line x1={A[0]} y1={A[1]} x2={C[0]} y2={C[1]} stroke="#c0392b" strokeWidth="1.8" strokeDasharray="5,4"/>
          <line x1={B[0]} y1={B[1]} x2={D[0]} y2={D[1]} stroke="#e07b00" strokeWidth="1.8" strokeDasharray="5,4"/>

          {/* Midpoint dot */}
          <circle cx={mid[0]} cy={mid[1]} r={5} fill="#9b59b6" stroke="#fff" strokeWidth="1.5"/>
          <text x={mid[0]+8} y={mid[1]-6} fontSize="10" fill="#9b59b6" fontWeight="700">midpoint</text>
          <text x={mid[0]+8} y={mid[1]+6} fontSize="9" fill="#9b59b6">(diagonals bisect each other)</text>

          {/* Diagonal labels */}
          <text x={(A[0]+C[0])/2-40} y={(A[1]+C[1])/2+18} fontSize="11" fill="#c0392b" fontWeight="700">d₁</text>
          <text x={(B[0]+D[0])/2+8} y={(B[1]+D[1])/2-6} fontSize="11" fill="#e07b00" fontWeight="700">d₂</text>

          {/* Vertex labels */}
          <text x={A[0]-16} y={A[1]+5}  fontSize="13" fill="#1F3864" fontWeight="700">A</text>
          <text x={B[0]+6}  y={B[1]+5}  fontSize="13" fill="#1F3864" fontWeight="700">B</text>
          <text x={C[0]+6}  y={C[1]-2}  fontSize="13" fill="#1F3864" fontWeight="700">C</text>
          <text x={D[0]-18} y={D[1]-2}  fontSize="13" fill="#1F3864" fontWeight="700">D</text>

          {/* Base label */}
          <text x={(A[0]+B[0])/2} y={A[1]+20} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">base b</text>

          {/* Side a label */}
          <text x={(A[0]+D[0])/2-22} y={(A[1]+D[1])/2+4} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">a</text>

          {/* Angle phi at A */}
          {(() => {
            const r = 32;
            const t1 = 0, t2 = Math.atan2(-(D[1]-A[1]), D[0]-A[0]);
            return (
              <g>
                <path d={"M "+(A[0]+r)+","+A[1]+" A "+r+" "+r+" 0 0 0 "+(A[0]+r*Math.cos(t2))+","+(A[1]+r*Math.sin(-t2))}
                  fill="none" stroke="#e07b00" strokeWidth="1.8"/>
                <text x={A[0]+38} y={A[1]-12} fontSize="11" fill="#e07b00" fontWeight="700">{"φ = "+phiDeg+"°"}</text>
              </g>
            );
          })()}

          {/* Supplementary angle at B */}
          {(() => {
            const r = 28;
            const t2 = Math.atan2(-(C[1]-B[1]), C[0]-B[0]);
            return (
              <g>
                <path d={"M "+(B[0]-r)+","+B[1]+" A "+r+" "+r+" 0 0 1 "+(B[0]+r*Math.cos(Math.PI-t2))+","+(B[1]-r*Math.sin(Math.PI-t2))}
                  fill="none" stroke="#9b59b6" strokeWidth="1.5"/>
                <text x={B[0]-66} y={B[1]-14} fontSize="10" fill="#9b59b6" fontWeight="700">{"180°-φ"}</text>
              </g>
            );
          })()}

          {/* Perimeter formula */}
          <rect x={W-180} y={10} width={170} height={52} rx={4} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-95} y={26} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Key Formulas</text>
          <text x={W-172} y={42} fontSize="10" fill="#2E75B6" fontFamily="'Courier New',monospace">P = 2(a + b)</text>
          <text x={W-172} y={56} fontSize="10" fill="#27ae60" fontFamily="'Courier New',monospace">A = b·h = a·b·sin(φ)</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        A parallelogram has two pairs of parallel sides. Opposite sides are equal, opposite angles are equal, and consecutive angles are supplementary (sum to 180°). The two diagonals always bisect each other at their midpoint.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 2 — Special Cases Family Tree
// ─────────────────────────────────────────────────────────────────
function DiagramSpecialCases() {
  const W = 520, H = 290;

  const shapes = [
    {
      label:'Parallelogram', sublabel:'base shape', constraint:'2 pairs parallel sides',
      color:'#2E75B6', x:185, y:20, w:150, h:40,
      pts:[[195,72],[335,72],[355,32],[215,32]],
    },
    {
      label:'Rectangle', sublabel:'all angles 90°', constraint:'+ all angles = 90°',
      color:'#27ae60', x:30, y:130, w:140, h:40,
      pts:[[30,178],[170,178],[170,138],[30,138]],
    },
    {
      label:'Rhombus', sublabel:'all sides equal', constraint:'+ all sides equal',
      color:'#c0392b', x:190, y:130, w:140, h:40,
      pts:[[260,178],[310,148],[260,118],[210,148]],
    },
    {
      label:'Square', sublabel:'angles 90° + sides equal', constraint:'rectangle AND rhombus',
      color:'#e07b00', x:30, y:235, w:140, h:40,
      pts:[[30,280],[170,280],[170,240],[30,240]],
    },
  ];

  // formula rows per shape
  const formulas = {
    'Parallelogram': ['A = b·h', 'P = 2(a+b)', 'd₁²+d₂²=2(a²+b²)'],
    'Rectangle':     ['A = l·w', 'P = 2(l+w)', 'd = √(l²+w²)'],
    'Rhombus':       ['A = d₁·d₂/2', 'P = 4a', 'd₁⊥d₂'],
    'Square':        ['A = s²', 'P = 4s', 'd = s√2'],
  };

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Parallelogram Family Tree — Special Cases and Their Extra Properties</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          {/* Arrows: parallelogram → rectangle, → rhombus */}
          <line x1={245} y1={72} x2={130} y2={130} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4,3"/>
          <line x1={275} y1={72} x2={275} y2={130} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4,3"/>
          {/* rectangle → square */}
          <line x1={100} y1={178} x2={100} y2={235} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4,3"/>
          {/* rhombus → square */}
          <line x1={260} y1={178} x2={130} y2={235} stroke="#bbb" strokeWidth="1.5" strokeDasharray="4,3"/>

          {shapes.map(({ label, sublabel, constraint, color, x, y, w, h, pts }) => (
            <g key={label}>
              {/* Shape preview */}
              <polygon points={pts.map(p=>p.join(',')).join(' ')} fill={color} fillOpacity="0.12" stroke={color} strokeWidth="2"/>
              {/* Info box */}
              <rect x={x+w+14} y={y} width={190} height={h+2} rx={4} fill="#fff" stroke={color} strokeWidth="1.5"/>
              <text x={x+w+109} y={y+16} textAnchor="middle" fontSize="11" fill={color} fontWeight="700">{label}</text>
              <text x={x+w+109} y={y+30} textAnchor="middle" fontSize="9.5" fill="#555">{constraint}</text>
              {formulas[label].map((f,i) => (
                <text key={i} x={x+w+22} y={y+44+i*14} fontSize="9" fill="#444" fontFamily="'Courier New',monospace">{f}</text>
              ))}
            </g>
          ))}

          {/* Square "both" label */}
          <text x={100} y={228} textAnchor="middle" fontSize="9" fill="#e07b00">rectangle + rhombus</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Every rectangle, rhombus, and square is also a parallelogram — each adds one more constraint. A rectangle adds right angles. A rhombus makes all sides equal. A square requires both, making it the most constrained member of the family.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 3 — Area Proof (cut and rearrange)
// ─────────────────────────────────────────────────────────────────
function DiagramAreaProof() {
  const W = 520, H = 260;
  const off = 60, base = 180, ht = 110;

  // Left: parallelogram ABCD
  const lx = 40;
  const A1=[lx,200], B1=[lx+base,200], C1=[lx+base+off,200-ht], D1=[lx+off,200-ht];

  // Right: the reassembled rectangle
  const rx = 300;
  const A2=[rx,200], B2=[rx+base,200], C2=[rx+base,200-ht], D2=[rx,200-ht];

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Area Proof — Why A = base × height</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <defs>
            <marker id="proofArr" markerWidth="8" markerHeight="8" refX="7" refY="4" orient="auto"><polygon points="0 0,8 4,0 8" fill="#555"/></marker>
          </defs>

          {/* ── LEFT: Parallelogram with triangle highlighted ── */}
          <text x={lx+base/2+off/2} y={22} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Step 1: Original parallelogram</text>

          {/* Main parallelogram */}
          <polygon points={[A1,B1,C1,D1].map(p=>p.join(',')).join(' ')} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2"/>

          {/* Triangle to cut off — left side: A1, foot of height from D1, D1 */}
          {(() => {
            const foot = [A1[0], D1[1]]; // foot of perpendicular from D1 to base extended
            return (
              <g>
                <polygon points={A1.join(',')+' '+foot.join(',')+' '+D1.join(',')} fill="#c0392b" fillOpacity="0.25" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>
                <text x={A1[0]+14} y={(A1[1]+D1[1])/2+4} fontSize="10" fill="#c0392b" fontWeight="700">cut</text>
                {/* Height line */}
                <line x1={foot[0]} y1={foot[1]} x2={D1[0]} y2={D1[1]} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="4,3"/>
                <polyline points={(foot[0]+10)+","+foot[1]+" "+(foot[0]+10)+","+(foot[1]-10)+" "+foot[0]+","+(foot[1]-10)} fill="none" stroke="#27ae60" strokeWidth="1.3"/>
                <text x={foot[0]-22} y={(foot[1]+D1[1])/2+4} fontSize="11" fill="#27ae60" fontWeight="700">h</text>
              </g>
            );
          })()}

          {/* Base label */}
          <text x={(A1[0]+B1[0])/2} y={A1[1]+20} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">base</text>

          {/* Arrow between diagrams */}
          <line x1={255} y1={140} x2={285} y2={140} stroke="#555" strokeWidth="2" markerEnd="url(#proofArr)"/>
          <text x={270} y={133} textAnchor="middle" fontSize="10" fill="#555">move</text>
          <text x={270} y={155} textAnchor="middle" fontSize="10" fill="#555">triangle</text>

          {/* ── RIGHT: Rectangle ── */}
          <text x={rx+base/2} y={22} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Step 2: Same area — rectangle!</text>

          <rect x={rx} y={200-ht} width={base} height={ht} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2"/>

          {/* Moved triangle pasted on right */}
          <polygon points={[B2,[rx+base,200-ht],[rx+base+off,200-ht]].map(p=>p.join(',')).join(' ')} fill="#c0392b" fillOpacity="0.25" stroke="#c0392b" strokeWidth="1.5" strokeDasharray="4,3"/>

          {/* Right angle marks */}
          <polyline points={rx+","+200+" "+(rx+10)+","+200+" "+(rx+10)+","+(200-10)} fill="none" stroke="#1F3864" strokeWidth="1.3"/>
          <polyline points={rx+","+(200-ht)+" "+(rx+10)+","+(200-ht)+" "+(rx+10)+","+(200-ht+10)} fill="none" stroke="#1F3864" strokeWidth="1.3"/>

          {/* Dimensions */}
          <text x={rx+base/2} y={200+20} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">base</text>
          <text x={rx-14} y={200-ht/2+4} textAnchor="end" fontSize="11" fill="#27ae60" fontWeight="700">h</text>

          {/* Result */}
          <rect x={rx} y={H-38} width={base} height={30} rx={4} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.5"/>
          <text x={rx+base/2} y={H-19} textAnchor="middle" fontSize="12" fill="#2E75B6" fontWeight="700">A = base × h = b·h</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        Cut the left triangle off the parallelogram and paste it onto the right side — the result is a rectangle with the same base and height. This proves A = base × height. Note: height is the perpendicular distance, not the slant side length.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 4 — Diagonal Properties
// ─────────────────────────────────────────────────────────────────
function DiagramDiagonals() {
  const W = 520, H = 270;
  const off = 65, base = 240, ht = 120;
  const A=[70,230], B=[70+base,230], C=[70+base+off,230-ht], D=[70+off,230-ht];
  const mid=[(A[0]+C[0])/2,(A[1]+C[1])/2];

  const a = Math.sqrt((A[0]-D[0])**2+(A[1]-D[1])**2);
  const b = base;
  const d1 = Math.sqrt((A[0]-C[0])**2+(A[1]-C[1])**2);
  const d2 = Math.sqrt((B[0]-D[0])**2+(B[1]-D[1])**2);

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Diagonal Properties — Bisection, Lengths, and the Sum Formula</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          {/* Parallelogram */}
          <polygon points={[A,B,C,D].map(p=>p.join(',')).join(' ')} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2.3"/>

          {/* Diagonal d1: A to C */}
          <line x1={A[0]} y1={A[1]} x2={C[0]} y2={C[1]} stroke="#c0392b" strokeWidth="2.2"/>
          {/* Diagonal d2: B to D */}
          <line x1={B[0]} y1={B[1]} x2={D[0]} y2={D[1]} stroke="#e07b00" strokeWidth="2.2"/>

          {/* Midpoint */}
          <circle cx={mid[0]} cy={mid[1]} r={6} fill="#9b59b6" stroke="#fff" strokeWidth="2"/>

          {/* Half-segment tick marks on each diagonal */}
          {[[A,mid],[mid,C]].map(([p1,p2],i) => {
            const mx=(p1[0]+p2[0])/2, my=(p1[1]+p2[1])/2;
            const dx=p2[0]-p1[0], dy=p2[1]-p1[1];
            const len=Math.sqrt(dx*dx+dy*dy);
            const nx=-dy/len*7, ny=dx/len*7;
            return <line key={i} x1={mx+nx} y1={my+ny} x2={mx-nx} y2={my-ny} stroke="#c0392b" strokeWidth="2"/>;
          })}
          {[[B,mid],[mid,D]].map(([p1,p2],i) => {
            const mx=(p1[0]+p2[0])/2, my=(p1[1]+p2[1])/2;
            const dx=p2[0]-p1[0], dy=p2[1]-p1[1];
            const len=Math.sqrt(dx*dx+dy*dy);
            const nx=-dy/len*7, ny=dx/len*7;
            return <line key={i} x1={mx+nx} y1={my+ny} x2={mx-nx} y2={my-ny} stroke="#e07b00" strokeWidth="2"/>;
          })}

          {/* Diagonal labels */}
          <text x={(A[0]+C[0])/2-30} y={(A[1]+C[1])/2+16} fontSize="12" fill="#c0392b" fontWeight="700">d₁ = {d1.toFixed(1)}</text>
          <text x={(B[0]+D[0])/2+10} y={(B[1]+D[1])/2-8} fontSize="12" fill="#e07b00" fontWeight="700">d₂ = {d2.toFixed(1)}</text>

          {/* Midpoint label */}
          <text x={mid[0]+10} y={mid[1]-8} fontSize="10" fill="#9b59b6" fontWeight="700">M — midpoint of both diagonals</text>

          {/* Side labels */}
          <text x={(A[0]+B[0])/2} y={A[1]+18} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">b = {b}</text>
          <text x={(A[0]+D[0])/2-20} y={(A[1]+D[1])/2+4} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">a = {a.toFixed(1)}</text>

          {/* Formula panel */}
          <rect x={W-195} y={10} width={188} height={130} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={W-101} y={27} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Diagonal Formulas</text>
          {[
            ['d₁² + d₂²  =  2(a² + b²)', '#1F3864'],
            ['d₁ = √(a²+b²-2ab·cosφ)', '#c0392b'],
            ['d₂ = √(a²+b²+2ab·cosφ)', '#e07b00'],
            ['Diagonals bisect each other','#9b59b6'],
            ['AM = MC,   BM = MD','#9b59b6'],
          ].map(([eq,col],i) => (
            <text key={i} x={W-188} y={44+i*18} fontSize="9.5" fill={col} fontFamily="'Courier New',monospace">{eq}</text>
          ))}

          {/* Numerical check */}
          <rect x={W-195} y={148} width={188} height={52} rx={4} fill="#f0fff4" stroke="#27ae60" strokeWidth="1"/>
          <text x={W-101} y={163} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Check: d₁²+d₂² = 2(a²+b²)</text>
          <text x={W-188} y={178} fontSize="9" fill="#27ae60" fontFamily="'Courier New',monospace">{d1.toFixed(1)}²+{d2.toFixed(1)}² = {(d1*d1+d2*d2).toFixed(0)}</text>
          <text x={W-188} y={192} fontSize="9" fill="#27ae60" fontFamily="'Courier New',monospace">2({a.toFixed(1)}²+{b}²) = {(2*(a*a+b*b)).toFixed(0)}</text>
        </svg>
      </div>
      <div style={S.dCaption}>
        The diagonals of any parallelogram bisect each other — they cut each other exactly in half at their intersection. The sum of the squares of the diagonals equals twice the sum of the squares of the two side lengths.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 5 — Trapezoids
// ─────────────────────────────────────────────────────────────────
function DiagramTrapezoids() {
  const W = 520, H = 270;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Trapezoids — One Pair of Parallel Sides, Median Line, and Special Cases</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>

          {/* ── General trapezoid ── */}
          <rect x={10} y={10} width={230} height={248} rx={5} fill="#f8fbff" stroke="#ccd9ea"/>
          <text x={125} y={28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">General Trapezoid</text>

          {(() => {
            const a1=[30,190], b1=[210,190], c1=[180,90], d1=[70,90];
            const a_len=180, b_len=110, ht=100;
            const mid_y=(190+90)/2;
            const mid_x1=(a1[0]+d1[0])/2, mid_x2=(b1[0]+c1[0])/2;
            return (
              <g>
                <polygon points={[a1,b1,c1,d1].map(p=>p.join(',')).join(' ')} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="2"/>
                {/* Parallel marks */}
                {[[a1,b1],[d1,c1]].map(([p1,p2],i) => {
                  const mx=(p1[0]+p2[0])/2, my=(p1[1]+p2[1])/2;
                  return <g key={i}><line x1={mx-6} y1={my-4} x2={mx+6} y2={my+4} stroke="#2E75B6" strokeWidth="1.8"/>{i===0&&<line x1={mx-3} y1={my-8} x2={mx+3} y2={my+8} stroke="#2E75B6" strokeWidth="1.8"/>}{i===1&&<><line x1={mx-3} y1={my-8} x2={mx+3} y2={my+8} stroke="#2E75B6" strokeWidth="1.8"/><line x1={mx-9} y1={my-4} x2={mx-3} y2={my+8} stroke="#2E75B6" strokeWidth="1.8"/></>}</g>;
                })}
                {/* Base labels */}
                <text x={(a1[0]+b1[0])/2} y={a1[1]+16} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">a (longer base)</text>
                <text x={(d1[0]+c1[0])/2} y={d1[1]-6} textAnchor="middle" fontSize="11" fill="#2E75B6" fontWeight="700">b (shorter base)</text>
                {/* Height */}
                <line x1={b1[0]+12} y1={b1[1]} x2={b1[0]+12} y2={c1[1]} stroke="#27ae60" strokeWidth="1.8" strokeDasharray="4,3"/>
                <text x={b1[0]+22} y={(b1[1]+c1[1])/2+4} fontSize="11" fill="#27ae60" fontWeight="700">h</text>
                {/* Median line */}
                <line x1={mid_x1} y1={mid_y} x2={mid_x2} y2={mid_y} stroke="#e07b00" strokeWidth="2" strokeDasharray="5,3"/>
                <text x={(mid_x1+mid_x2)/2} y={mid_y-6} textAnchor="middle" fontSize="10" fill="#e07b00" fontWeight="700">median m = (a+b)/2</text>
                {/* Formula */}
                <rect x={20} y={210} width={200} height={38} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
                <text x={120} y={225} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">A = (a + b)/2 × h</text>
                <text x={120} y={240} textAnchor="middle" fontSize="10" fill="#555">= median × height</text>
              </g>
            );
          })()}

          {/* ── Special cases: right and isosceles ── */}
          <rect x={250} y={10} width={260} height={248} rx={5} fill="#f8fbff" stroke="#ccd9ea"/>
          <text x={380} y={28} textAnchor="middle" fontSize="11" fill="#1F3864" fontWeight="700">Special Trapezoids</text>

          {/* Right trapezoid */}
          {(() => {
            const a1=[270,130], b1=[470,130], c1=[470,50], d1=[270,50];
            // right trap: left side vertical
            const pts=[[270,130],[460,130],[460,50],[270,50]];
            // slanted right side: shift top right
            const pts2=[[270,130],[460,130],[400,50],[270,50]];
            return (
              <g>
                <text x={365} y={46} textAnchor="middle" fontSize="10" fill="#c0392b" fontWeight="700">Right Trapezoid</text>
                <polygon points={pts2.map(p=>p.join(',')).join(' ')} fill="#fff0f0" stroke="#c0392b" strokeWidth="1.8"/>
                <polyline points="280,130 280,120 270,120" fill="none" stroke="#c0392b" strokeWidth="1.3"/>
                <text x={365} y={145} textAnchor="middle" fontSize="9" fill="#c0392b">one leg perpendicular to bases</text>
              </g>
            );
          })()}

          {/* Isosceles trapezoid */}
          {(() => {
            const pts=[[270,240],[480,240],[440,168],[310,168]];
            return (
              <g>
                <text x={375} y={162} textAnchor="middle" fontSize="10" fill="#27ae60" fontWeight="700">Isosceles Trapezoid</text>
                <polygon points={pts.map(p=>p.join(',')).join(' ')} fill="#f0fff4" stroke="#27ae60" strokeWidth="1.8"/>
                {/* Tick marks on equal legs */}
                {([[pts[0],pts[3]],[pts[1],pts[2]]]).map(([p1,p2],i) => {
                  const mx=(p1[0]+p2[0])/2, my=(p1[1]+p2[1])/2;
                  const dx=p2[0]-p1[0], dy=p2[1]-p1[1], len=Math.sqrt(dx*dx+dy*dy);
                  const nx=-dy/len*6, ny=dx/len*6;
                  return <line key={i} x1={mx+nx} y1={my+ny} x2={mx-nx} y2={my-ny} stroke="#27ae60" strokeWidth="2"/>;
                })}
                <text x={375} y={255} textAnchor="middle" fontSize="9" fill="#27ae60">equal legs, equal base angles</text>
                <text x={375} y={267} textAnchor="middle" fontSize="9" fill="#27ae60">diagonals are equal length</text>
              </g>
            );
          })()}
        </svg>
      </div>
      <div style={S.dCaption}>
        A trapezoid (trapezium) has exactly one pair of parallel sides called bases. The area formula uses the average of both bases times the height. The median line connects the midpoints of the legs and equals the average of the two bases.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// DIAGRAM 6 — Engineering Applications
// ─────────────────────────────────────────────────────────────────
function DiagramApplications() {
  const W = 520, H = 270;

  return (
    <div style={S.dWrap}>
      <div style={S.dTitle}>Parallelograms and Trapezoids Across Engineering</div>
      <div style={{ textAlign:'center' }}>
        <svg width={W} height={H} viewBox={"0 0 "+W+" "+H} style={{ maxWidth:'100%' }}>
          <defs>
            <marker id="appArr" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#1F3864"/></marker>
            <marker id="appArrB" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#2E75B6"/></marker>
            <marker id="appArrR" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#c0392b"/></marker>
            <marker id="appArrG" markerWidth="7" markerHeight="7" refX="6" refY="3.5" orient="auto"><polygon points="0 0,7 3.5,0 7" fill="#27ae60"/></marker>
          </defs>

          {/* ── Panel 1: Force parallelogram (vector addition) ── */}
          <rect x={10} y={10} width={155} height={245} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={87} y={26} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Force Addition</text>
          <text x={87} y={38} textAnchor="middle" fontSize="9" fill="#888">(Parallelogram Law)</text>
          {(() => {
            const ox=55, oy=155;
            const F1x=80, F1y=-30;   // F1 vector
            const F2x=30, F2y=-80;   // F2 vector
            const Rx=F1x+F2x, Ry=F1y+F2y; // resultant
            return (
              <g>
                {/* Parallelogram ghost */}
                <polygon points={ox+","+oy+" "+(ox+F1x)+","+(oy+F1y)+" "+(ox+Rx)+","+(oy+Ry)+" "+(ox+F2x)+","+(oy+F2y)}
                  fill="#EBF3FB" stroke="#ccd9ea" strokeWidth="1" strokeDasharray="4,3"/>
                {/* F1 */}
                <line x1={ox} y1={oy} x2={ox+F1x} y2={oy+F1y} stroke="#2E75B6" strokeWidth="2.5" markerEnd="url(#appArrB)"/>
                <text x={ox+F1x+5} y={oy+F1y+4} fontSize="11" fill="#2E75B6" fontWeight="700">F₁</text>
                {/* F2 */}
                <line x1={ox} y1={oy} x2={ox+F2x} y2={oy+F2y} stroke="#27ae60" strokeWidth="2.5" markerEnd="url(#appArrG)"/>
                <text x={ox+F2x+4} y={oy+F2y-4} fontSize="11" fill="#27ae60" fontWeight="700">F₂</text>
                {/* Dashed copies */}
                <line x1={ox+F1x} y1={oy+F1y} x2={ox+Rx} y2={oy+Ry} stroke="#27ae60" strokeWidth="1.2" strokeDasharray="4,3"/>
                <line x1={ox+F2x} y1={oy+F2y} x2={ox+Rx} y2={oy+Ry} stroke="#2E75B6" strokeWidth="1.2" strokeDasharray="4,3"/>
                {/* Resultant */}
                <line x1={ox} y1={oy} x2={ox+Rx-4} y2={oy+Ry-4} stroke="#c0392b" strokeWidth="2.5" markerEnd="url(#appArrR)"/>
                <text x={ox+Rx/2+8} y={oy+Ry/2} fontSize="11" fill="#c0392b" fontWeight="700">R</text>
                <text x={87} y={220} textAnchor="middle" fontSize="9" fill="#555">R = F₁ + F₂</text>
                <text x={87} y={232} textAnchor="middle" fontSize="9" fill="#555">diagonal = resultant</text>
              </g>
            );
          })()}

          {/* ── Panel 2: Parallelogram linkage ── */}
          <rect x={175} y={10} width={160} height={245} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={255} y={26} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Parallelogram Linkage</text>
          <text x={255} y={38} textAnchor="middle" fontSize="9" fill="#888">(keeps orientation fixed)</text>
          {(() => {
            const configs = [
              { off:0,   y:75  },
              { off:28,  y:130 },
              { off:50,  y:185 },
            ];
            const lx0=195, lw=120, lh=30;
            return (
              <g>
                {configs.map(({ off, y }, i) => {
                  const A=[lx0+off, y+lh], B=[lx0+off+lw, y+lh], C=[lx0+off+lw, y], D=[lx0+off, y];
                  return (
                    <g key={i}>
                      <polygon points={[A,B,C,D].map(p=>p.join(',')).join(' ')} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1.8"/>
                      {/* Pivots */}
                      {[A,B,C,D].map((p,j) => <circle key={j} cx={p[0]} cy={p[1]} r={4} fill="#e07b00"/>)}
                      {/* Coupler bar stays horizontal */}
                      <text x={lx0+off+lw/2} y={y+lh/2+4} textAnchor="middle" fontSize="9" fill="#2E75B6" fontWeight="700">horizontal</text>
                    </g>
                  );
                })}
                <text x={255} y={220} textAnchor="middle" fontSize="9" fill="#555">coupler bar always stays</text>
                <text x={255} y={232} textAnchor="middle" fontSize="9" fill="#555">parallel to fixed base</text>
              </g>
            );
          })()}

          {/* ── Panel 3: Trapezoidal cross-section (channel/dam) ── */}
          <rect x={345} y={10} width={165} height={245} rx={5} fill="#fff" stroke="#ccd9ea"/>
          <text x={427} y={26} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">Trapezoidal Channel</text>
          <text x={427} y={38} textAnchor="middle" fontSize="9" fill="#888">(civil / hydraulics)</text>
          {(() => {
            const bw=60, tw=120, ht=80, by=180, tx=367;
            const A=[tx+(tw-bw)/2, by], B=[tx+(tw-bw)/2+bw, by];
            const C=[tx+tw, by-ht], D=[tx, by-ht];
            return (
              <g>
                <polygon points={[A,B,C,D].map(p=>p.join(',')).join(' ')} fill="#d0e8ff" stroke="#2E75B6" strokeWidth="2"/>
                {/* Water level */}
                <line x1={D[0]} y1={D[1]} x2={C[0]} y2={C[1]} stroke="#2E75B6" strokeWidth="2.5"/>
                {/* Labels */}
                <text x={(A[0]+B[0])/2} y={by+16} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">b (base)</text>
                <text x={(D[0]+C[0])/2} y={D[1]-8} textAnchor="middle" fontSize="10" fill="#2E75B6" fontWeight="700">T (top width)</text>
                {/* Height */}
                <line x1={C[0]+10} y1={D[1]} x2={C[0]+10} y2={by} stroke="#27ae60" strokeWidth="1.5" strokeDasharray="3,2"/>
                <text x={C[0]+20} y={(D[1]+by)/2+4} fontSize="10" fill="#27ae60" fontWeight="700">h</text>
                {/* Side slope */}
                <text x={D[0]-8} y={(D[1]+A[1])/2} textAnchor="end" fontSize="9" fill="#e07b00">z:1</text>
                {/* Formula */}
                <rect x={353} y={200} width={148} height={44} rx={3} fill="#EBF3FB" stroke="#2E75B6" strokeWidth="1"/>
                <text x={427} y={215} textAnchor="middle" fontSize="10" fill="#1F3864" fontWeight="700">A = (b + T)/2 × h</text>
                <text x={427} y={229} textAnchor="middle" fontSize="9.5" fill="#555">T = b + 2zh</text>
                <text x={427} y={241} textAnchor="middle" fontSize="9" fill="#888">z = side slope ratio</text>
              </g>
            );
          })()}
        </svg>
      </div>
      <div style={S.dCaption}>
        Three classic engineering uses. The parallelogram law of vector addition uses the diagonal as the resultant of two forces. Parallelogram linkages keep the coupler bar's orientation constant regardless of position — used in pantographs, drafting arms, and robotic arms. Trapezoidal channels are the standard cross-section in irrigation and drainage design.
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────
// MAIN COMPONENT
// ─────────────────────────────────────────────────────────────────
export default function Parallelograms(props) {
  return (
    <div style={S.wrapper}>
      <h1 style={S.h1}>Parallelograms</h1>

      <p style={S.p}>
        A parallelogram is any quadrilateral with two pairs of parallel sides. It is one of the most important shapes in engineering — it is the geometric basis for vector addition, four-bar linkage mechanisms, and shear deformation. Rectangles, rhombuses, and squares are all special cases.
      </p>

      <InfoBox
        title="Parallelogram — Defining Properties"
        items={[
          "Two pairs of parallel sides: AB ∥ DC and AD ∥ BC",
          "Opposite sides are equal in length: AB = DC and AD = BC",
          "Opposite angles are equal: ∠A = ∠C and ∠B = ∠D",
          "Consecutive angles are supplementary: ∠A + ∠B = 180°",
          "Diagonals bisect each other — they cut each other exactly in half",
        ]}
      />

      <h2 style={S.h2}>Anatomy</h2>
      <DiagramAnatomy />
      <Table2 rows={[
        ["Sides",             "a and b — the two distinct side lengths\nOpposite sides are equal and parallel"],
        ["Base and height",   "Any side can be the base\nHeight h = perpendicular distance between the two parallel bases\nh is NOT the slant side length — it is always perpendicular"],
        ["Angles",            "φ and (180°-φ) alternate around the four corners\nOpposite angles are equal, adjacent angles are supplementary"],
        ["Diagonals d₁, d₂",  "Connect opposite vertices\nBisect each other — midpoints coincide at center M"],
        ["Perimeter",         "P = 2(a + b)"],
        ["Area",              "A = b·h = a·b·sinφ\nBoth formulas give the same result"],
      ]} />

      <h2 style={S.h2}>Special Cases</h2>
      <DiagramSpecialCases />
      <Table2 rows={[
        ["Rectangle",         "All four angles = 90°\nA = length × width\nDiagonals equal in length: d = √(l²+w²)\nDiagonals bisect each other but are NOT perpendicular"],
        ["Rhombus",           "All four sides equal length\nA = d₁·d₂/2  (product of diagonals divided by 2)\nDiagonals perpendicular to each other\nDiagonals bisect the corner angles"],
        ["Square",            "All sides equal AND all angles 90°\nA = s²,  P = 4s,  diagonal = s√2\nDiagonals: equal length, perpendicular, bisect each other and the angles"],
        ["Testing which type","Rectangle: check for right angles (slope product = -1 for adjacent sides)\nRhombus: check all four side lengths are equal\nSquare: both conditions satisfied"],
      ]} />

      <h2 style={S.h2}>Area Proof</h2>
      <DiagramAreaProof />
      <Table2 rows={[
        ["Area formula",      "A = base × height = b·h\nAlternative using angle: A = a·b·sinφ\n(when height is unknown but the angle and both sides are known)"],
        ["Height vs side",    "Height h is the perpendicular drop from one base to the other\nThe slant side a is always longer than h unless φ = 90°\nh = a·sinφ"],
        ["Using the angle",   "A = a·b·sinφ — useful in vectors and structural analysis\nMaximum area when φ = 90° (becomes a rectangle)\nZero area when φ = 0° or 180° (flat, degenerate)"],
        ["Cross product link","Area of parallelogram = |a × b| where a and b are edge vectors\nThis is the geometric meaning of the cross product magnitude"],
      ]} />

      <h2 style={S.h2}>Diagonal Properties</h2>
      <DiagramDiagonals />
      <Table2 rows={[
        ["Bisection",         "Diagonals always bisect each other in any parallelogram\nIf they are also equal → rectangle\nIf they are also perpendicular → rhombus\nIf both → square"],
        ["Diagonal lengths",  "d₁ = √(a²+b²-2ab·cosφ)\nd₂ = √(a²+b²+2ab·cosφ)\nDerived from the law of cosines on the two triangles formed"],
        ["Sum of squares",    "d₁² + d₂² = 2(a² + b²)\nA useful check — works for any parallelogram"],
        ["Midpoint theorem",  "The midpoints of any quadrilateral's sides form a parallelogram\n(Varignon's theorem)"],
      ]} />

      <h2 style={S.h2}>Trapezoids</h2>
      <DiagramTrapezoids />
      <Table2 rows={[
        ["Definition",        "Exactly one pair of parallel sides (the two bases)\nThe parallel sides are called bases a and b\nThe non-parallel sides are called legs"],
        ["Area",              "A = (a + b)/2 × h\n= average base × height\n= median × height"],
        ["Median line",       "Connects midpoints of the two legs\nLength = (a + b)/2 — average of the bases\nParallel to both bases"],
        ["Right trapezoid",   "One leg is perpendicular to both bases\nSimplifies area: A = (a+b)·h/2 with right angle at two corners"],
        ["Isosceles trapezoid","Legs are equal length\nBase angles are equal (both pairs)\nDiagonals are equal length\nSymmetric about the perpendicular bisector of the bases"],
        ["Engineering use",   "Trapezoidal cross-sections: irrigation channels, retaining wall footings,\ndam cross-sections — area A = (b+T)/2 × h where T = top width = b+2zh"],
      ]} />

      <h2 style={S.h2}>Engineering Applications</h2>
      <DiagramApplications />

      <InfoBox
        title="Key Takeaway — Where These Shapes Appear in Engineering"
        items={[
          "Vector addition: the parallelogram law — draw two force vectors from the same point, the diagonal of the parallelogram they form is the resultant.",
          "Four-bar linkages: a parallelogram linkage keeps the coupler bar's angle constant throughout motion — used in pantographs, drafting arms, suspension systems, and robotic end effectors.",
          "Shear deformation: a rectangle under shear stress deforms into a parallelogram — the shear strain γ is the angle change from 90°.",
          "Trapezoidal channels: the standard cross-section for open drainage and irrigation channels — area and flow capacity calculated from the trapezoid formula.",
          "Cross product: the magnitude of a×b equals the area of the parallelogram formed by vectors a and b — this is why torque and area both use the cross product.",
          "Signal processing: trapezoidal integration (area under a curve approximated by trapezoids) is one of the most common numerical methods.",
        ]}
      />
    </div>
  );
}